/**
 * AuthContext - Backend OAuth Authentication
 *
 * This context uses backend OAuth endpoints for authentication.
 * All authentication flows (login, logout) are handled by the backend.
 * The frontend never sees or handles tokens - only HttpOnly cookies.
 *
 * Key Features:
 * - Backend handles OAuth 2.0 Authorization Code flow with PKCE
 * - Cognito Managed UI for login/logout pages
 * - HttpOnly cookies for token storage (XSS protection)
 * - No localStorage token storage
 * - No Amplify SDK dependency
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { API_BASE_URL } from '../types/api'
import { getAppPrefs, setAppPrefs } from '../utils/prefs'

// Group membership information from database enrichment
export type GroupMembership = {
  group_id: string
  group_slug: string
  group_name: string
  tenant_id: number
  metadata: Record<string, unknown> // Contains user roles for this group
}

// User data type with all claims from backend
// Includes both JWT claims and database-enriched information
export type UserData = {
  // From JWT (Cognito)
  email: string
  given_name?: string
  family_name?: string

  // From Database (enriched after JWT validation)
  user_id?: string // User's UUID from database
  groups?: GroupMembership[] // User's group memberships with roles
  tenant_names?: string[] // Accessible tenant names (for URL matching)
  is_enriched?: boolean // True if database enrichment was performed
}

// Authentication state type
type AuthState = {
  isAuthenticated: boolean
  isInitialized: boolean
  user: UserData | null
}

// Context value type
type AuthContextValue = {
  // State
  isAuthenticated: boolean
  isInitialized: boolean
  user: UserData | null
  isLoginDisabled: boolean
  isDevelopmentMode: boolean

  // OAuth methods
  login: () => void
  logout: () => void

  // Session check
  checkSession: () => Promise<void>

  // Phase toggles (for Settings page)
  setLoginDisabled: (disabled: boolean) => void
  setDevelopmentMode: (enabled: boolean) => void
  isPhaseFutureEnabled: boolean
  setPhaseFutureEnabled: (enabled: boolean) => void
}

// Create context
const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// Storage keys for persisting settings
const LOGIN_DISABLED_KEY = 'login_disabled'
const DEVELOPMENT_MODE_KEY = 'development_mode'
const PHASE_FUTURE_KEY = 'phase_future_enabled'

/**
 * AuthProvider Component
 */
export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.JSX.Element => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isInitialized: false,
    user: null,
  })

  // Phase feature flags (stored in localStorage for Settings page)
  const [isLoginDisabled, setIsLoginDisabled] = useState(() => {
    return getAppPrefs<boolean>(LOGIN_DISABLED_KEY) ?? false
  })
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(() => {
    return getAppPrefs<boolean>(DEVELOPMENT_MODE_KEY) ?? false
  })
  const [isPhaseFutureEnabled, setIsPhaseFutureEnabled] = useState(() => {
    return getAppPrefs<boolean>(PHASE_FUTURE_KEY) ?? false
  })

  // Check if login is disabled from env (demo mode)
  const isLoginDisabledEnv = import.meta.env.VITE_DISABLE_LOGIN === 'true'
  const isDevelopmentModeEnv = import.meta.env.DEV && isLoginDisabledEnv
  // Get dev auth settings from env with defaults
  const devAuthRole = import.meta.env.VITE_DEV_AUTH_ROLE || 'super_admin'
  const devAuthTenant = import.meta.env.VITE_DEV_AUTH_TENANT || 'dev-tenant'

  // Wrapper setters that also persist to localStorage using getAppPrefs/setAppPrefs
  const setLoginDisabledPersistent = (disabled: boolean) => {
    setIsLoginDisabled(disabled)
    setAppPrefs(disabled, LOGIN_DISABLED_KEY)
  }

  const setDevelopmentModePersistent = (enabled: boolean) => {
    setIsDevelopmentMode(enabled)
    setAppPrefs(enabled, DEVELOPMENT_MODE_KEY)
  }

  const setPhaseFutureEnabledPersistent = (enabled: boolean) => {
    setIsPhaseFutureEnabled(enabled)
    setAppPrefs(enabled, PHASE_FUTURE_KEY)
  }

  /**
   * Login: redirect to backend OAuth endpoint
   * Backend will handle PKCE and redirect to Cognito Hosted UI
   */
  const login = useCallback(() => {
    // Auth endpoints are tenant-agnostic
    window.location.href = `${API_BASE_URL}/auth/login`
  }, [])

  /**
   * Check current session status from backend
   * This endpoint returns session info without exposing tokens
   */
  const checkSession = useCallback(async () => {
    try {
      // Auth endpoints are tenant-agnostic
      const response = await fetch(`${API_BASE_URL}/auth/session`, {
        credentials: 'include', // Send HttpOnly cookies
      })

      if (response.ok) {
        const data = await response.json()
        setAuthState({
          isAuthenticated: data.authenticated,
          isInitialized: true,
          user: data.user || null,
        })
        console.log('✅ Session check successful:', data)

        // Check for redirect after login
        const redirectPath = sessionStorage.getItem('auth_redirect_after_login')
        if (redirectPath && redirectPath !== '/logout' && data.authenticated) {
          sessionStorage.removeItem('auth_redirect_after_login')

          // Validate redirect path to prevent open redirect vulnerabilities
          // Only allow relative paths (no protocol, no //)
          if (
            redirectPath.startsWith('/') &&
            !redirectPath.startsWith('//') &&
            !redirectPath.includes('://')
          ) {
            console.log('🔄 Redirecting to saved path:', redirectPath)
            window.location.href = redirectPath
          } else {
            console.warn(
              '⚠️  Invalid redirect path blocked (potential open redirect):',
              redirectPath,
            )
          }
        }
      } else {
        // Check if this is a session expiry (vs just not logged in)
        if (response.status === 401) {
          const authError = response.headers.get('X-Auth-Error')
          if (authError === 'token-expired' || authError === 'token-missing') {
            // Save current path for redirect after login
            const currentPath =
              window.location.pathname + window.location.search
            sessionStorage.setItem('auth_redirect_after_login', currentPath)

            // Clear auth state
            setAuthState({
              isAuthenticated: false,
              isInitialized: true,
              user: null,
            })

            // Redirect to login
            login()
            return
          }
        }

        // Not authenticated (normal case)
        setAuthState({
          isAuthenticated: false,
          isInitialized: true,
          user: null,
        })
        console.log('ℹ️  No active session')
      }
    } catch (error) {
      console.error('Session check failed:', error)
      setAuthState({
        isAuthenticated: false,
        isInitialized: true,
        user: null,
      })
    }
  }, [login])

  /**
   * Initialize - check session on mount
   */
  useEffect(() => {
    // Check if login is disabled from env (demo mode)
    if (isLoginDisabledEnv) {
      console.log('⚠️  Authentication disabled - demo mode', {
        role: devAuthRole,
        tenant: devAuthTenant,
      })
      // Inject mock user data to match backend dev mode
      // Note: In dev mode, the backend still sets legacy custom attributes for compatibility,
      // but the frontend should use the database-enriched fields (groups, tenant_names)
      setAuthState({
        isAuthenticated: true,
        isInitialized: true,
        user: {
          email: 'dev@rooncyber.com',
          given_name: 'Dev',
          family_name: 'User',
          // Mock database-enriched fields
          user_id: 'dev-user-id',
          groups: [
            {
              group_id: 'dev-group',
              group_slug: devAuthTenant,
              group_name: 'Development Group',
              tenant_id: 1,
              metadata: { role: devAuthRole },
            },
          ],
          tenant_names: [devAuthTenant],
          is_enriched: true,
        },
      })
      return
    }

    // Check initial session status
    checkSession()
  }, [isLoginDisabledEnv, devAuthRole, devAuthTenant, checkSession])

  /**
   * Handle session expiration events from API calls
   */
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent
      console.log(
        '🔐 Session expired, redirecting to login',
        customEvent.detail,
      )

      // Clear auth state
      setAuthState({
        isAuthenticated: false,
        isInitialized: true,
        user: null,
      })

      // Redirect to login (will preserve return URL in sessionStorage)
      login()
    }

    window.addEventListener(
      'auth:session-expired',
      handleSessionExpired as EventListener,
    )

    return () => {
      window.removeEventListener(
        'auth:session-expired',
        handleSessionExpired as EventListener,
      )
    }
  }, [login])

  /**
   * Logout: POST to backend logout endpoint with CSRF protection
   * Backend will clear cookie and redirect to Cognito logout
   *
   * Note: We use a form submission to allow the browser to navigate through
   * the full redirect chain (Backend -> Cognito -> Frontend /logout page).
   * The CSRF token is sent in the form body (backend accepts both header and body).
   * This is necessary because fetch() doesn't change the browser's location,
   * it only follows redirects internally.
   */
  const logout = () => {
    console.log('👋 Logging out via form POST to backend')

    try {
      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrf_token='))
        ?.split('=')[1]

      if (!csrfToken) {
        console.warn('No CSRF token found, logout may fail')
      }

      // Create a hidden form to POST to logout endpoint
      // This allows the browser to navigate through the redirect chain:
      // Frontend -> Backend -> Cognito -> Frontend /logout page
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${API_BASE_URL}/auth/logout`

      // Add CSRF token as hidden input
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'csrf_token'
      csrfInput.value = csrfToken || ''
      form.appendChild(csrfInput)

      // Add form to body and submit
      document.body.appendChild(form)
      form.submit()
    } catch (error) {
      console.error('Logout error:', error)
      // Fallback: redirect to logout page
      window.location.href = '/logout'
    }
  }

  // Context value
  const value: AuthContextValue = {
    // State
    isAuthenticated: authState.isAuthenticated,
    isInitialized: authState.isInitialized,
    user: authState.user,
    isLoginDisabled: isLoginDisabledEnv || isLoginDisabled,
    isDevelopmentMode: isDevelopmentModeEnv || isDevelopmentMode,

    // Methods
    login,
    logout,
    checkSession,

    // Phase toggles
    setLoginDisabled: setLoginDisabledPersistent,
    setDevelopmentMode: setDevelopmentModePersistent,
    isPhaseFutureEnabled,
    setPhaseFutureEnabled: setPhaseFutureEnabledPersistent,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuth Hook
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
